import React, { useContext, useEffect } from 'react';
import { CartState } from './types';

export const DEFAULT_CART_STATE: CartState = {
  items: [],
  message: '',
  shippingDetails: {
    name: '',
    phoneNumber: '',
    email: '',
    company: '',
    street: '',
    street2: '',
    postCode: '',
    city: '',
    country: ''
  }
};

const CartContext = React.createContext<{
  value: CartState;
  setValue: React.Dispatch<React.SetStateAction<CartState>>;
  drawerOpen: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}>({
  value: DEFAULT_CART_STATE,
  setValue: () => {},
  drawerOpen: false,
  setDrawerOpen: () => false
});

const LOCAL_STORAGE_KEY = 'cartContent';

export const CartProvider: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [value, setValue] = React.useState<CartState>(() => {
    // read from localStorage initially
    const storedValue = localStorage.getItem(LOCAL_STORAGE_KEY);
    return storedValue ? JSON.parse(storedValue) : DEFAULT_CART_STATE;
  });

  // listen to localStorage to receive updates from other tabs
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === LOCAL_STORAGE_KEY) {
        const newCart = event.newValue ? JSON.parse(event.newValue) : null;
        if (newCart) setValue(newCart);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // persist changes to localStorage
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(value));
  }, [value]);

  return (
    <CartContext.Provider
      value={{ value, setValue, drawerOpen, setDrawerOpen }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCartState = () => useContext(CartContext);
